import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import ReportService from '@services/ReportService';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import type { ConversationCard } from '@/types/dashboard';
import './AvailabilityReport.css';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import EditCard from '@components/EditCard/EditCard';
import { authStorage } from '@lib/storage';
import { config } from '@config/env';
import { getSideMenuLocal } from '@/components/utils/CorgeeHelper';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: number | string;
  label: string;
}

interface VacationRequest {
  VacationRequestId: number;
  PersonId: number;
  DateFrom: string;
  DateTo: string;
  Status: number;
  RequestTypeName: string;
}

interface AssignmentItem {
  PersonID: number;
  NamePerson?: string;
  Name?: string;
  Team?: string;
  TeamId?: number | null;
  ProjectID?: number;
  ProjectName?: string;
  Estimated?: number;
  IsAssignedCard?: number;
  CARDSD?: string | null;
  CARDDD?: string | null;
  ConversationId?: number;
  Title?: string;
  [key: string]: any;
}

type CellType = 'weekend' | 'vacation' | 'assigned' | 'gray' | 'white';

interface DayCol {
  label: string;
  dateStr: string | null;
  isWeekend: boolean;
}

interface ConversationRef {
  id: number;
  title: string;
}

interface ProjectRow {
  projectId: number;
  projectName: string;
  dayCells: { value: string; type: CellType; conversations: ConversationRef[] }[];
}

interface PersonRow {
  personId: number;
  name: string;
  team: string;
  cfValues: string[];
  dayCells: { value: string; type: CellType }[];
  projects: ProjectRow[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stripHtml(html: string | null | undefined): string
{
  if (!html) return '-';
  const div = document.createElement('div');
  div.innerHTML = String(html);
  const text = div.textContent || div.innerText || '';
  return text.trim() || '-';
}

function cfKeyToHeader(key: string): string
{
  return key.replaceAll('cf', '').replaceAll('_', '').trim();
}

function getEstimatedPerDay(
  startDate: Date,
  dueDate: Date,
  estimated: number,
  vacations: VacationRequest[],
): number
{
  const diff = (dueDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  let workDays = 0;
  const cur = new Date(startDate);
  for (let i = 0; i < diff; i++)
  {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6)
    {
      let isVac = false;
      for (const v of vacations)
      {
        if (v.Status !== 1) continue;
        const s = Date.parse(v.DateFrom.slice(0, 10));
        const e = Date.parse(v.DateTo.slice(0, 10));
        if (cur.getTime() >= s && cur.getTime() <= e)
        {
          isVac = true;
          break;
        }
      }
      if (!isVac) workDays++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return workDays > 0 ? estimated / workDays : 0;
}

function isVacDay(d: Date, vacations: VacationRequest[]): boolean
{
  const t = Date.parse(d.toISOString().slice(0, 10));
  for (const v of vacations)
  {
    if (v.Status !== 1) continue;
    const s = Date.parse(v.DateFrom.slice(0, 10));
    const e = Date.parse(v.DateTo.slice(0, 10));
    if (t >= s && t <= e) return true;
  }
  return false;
}

function processReport(
  items: AssignmentItem[],
  unassigned: AssignmentItem[],
  vacations: VacationRequest[],
  fromDate: Date,
  toDate: Date,
  teamMap: Map<number, string>,
): { dayCols: DayCol[]; cfHeaders: string[]; personRows: PersonRow[] }
{
  const diffDays = moment(toDate).diff(moment(fromDate), 'days') + 1;
  const dayCols: DayCol[] = [];
  const dayDates: (Date | null)[] = [];
  const cur = new Date(fromDate);

  for (let i = 0; i < diffDays; i++)
  {
    const dow = cur.getDay();
    if (dow === 0 || dow === 6)
    {
      dayCols.push({ label: '-', dateStr: null, isWeekend: true });
      dayDates.push(null);
    }
    else
    {
      const month = cur.toLocaleDateString('en-US', { month: 'short' });
      dayCols.push({
        label: `${month} ${cur.getDate()}`,
        dateStr: new Date(cur).toISOString(),
        isWeekend: false,
      });
      dayDates.push(new Date(cur));
    }
    cur.setDate(cur.getDate() + 1);
  }

  // Extract cf_ headers
  const cfHeaders: string[] = [];
  if (items[0])
  {
    for (const key of Object.keys(items[0]))
    {
      if (key.startsWith('cf_')) cfHeaders.push(key);
    }
  }

  // Group by PersonID
  const personMap = new Map<number, AssignmentItem[]>();
  for (const item of items)
  {
    if (!personMap.has(item.PersonID)) personMap.set(item.PersonID, []);
    personMap.get(item.PersonID)!.push(item);
  }
  for (const u of unassigned)
  {
    if (!personMap.has(u.PersonID)) personMap.set(u.PersonID, [u]);
  }

  const personRows: PersonRow[] = [];

  for (const [personId, personItems] of personMap.entries())
  {
    const firstItem = personItems[0]!;
    const personVacs = vacations.filter(v => v.PersonId === personId);

    const name = firstItem.NamePerson || firstItem.Name || '';
    let team = firstItem.Team || '';
    if (!team && firstItem.TeamId) team = teamMap.get(firstItem.TeamId) || '';

    const cfValues = cfHeaders.map(key => stripHtml(firstItem[key]));

    // Group tasks by ProjectID
    const projMap = new Map<number, AssignmentItem[]>();
    for (const item of personItems)
    {
      if (item.ProjectID == null) continue;
      if (!projMap.has(item.ProjectID)) projMap.set(item.ProjectID, []);
      projMap.get(item.ProjectID)!.push(item);
    }

    const projectRows: ProjectRow[] = [];

    for (const [projId, projItems] of projMap.entries())
    {
      const projName = projItems[0]!.ProjectName || '';
      const projVals = new Array<number>(diffDays).fill(0);

      for (const task of projItems)
      {
        if (!task.CARDSD || !task.CARDDD || task.Estimated == null) continue;
        const cardStart = new Date(task.CARDSD);
        const cardDue = new Date(task.CARDDD);
        cardDue.setDate(cardDue.getDate() + 1);
        const epd = getEstimatedPerDay(
          new Date(cardStart),
          new Date(cardDue),
          task.Estimated,
          personVacs,
        );
        for (let i = 0; i < diffDays; i++)
        {
          if (dayCols[i]!.isWeekend) continue;
          const d = dayDates[i]!;
          if (d >= cardStart && d < cardDue) projVals[i]! += epd;
        }
      }

      const projDayCells: ProjectRow['dayCells'] = [];
      for (let i = 0; i < diffDays; i++)
      {
        if (dayCols[i]!.isWeekend)
        {
          projDayCells.push({ value: '-', type: 'weekend', conversations: [] });
          continue;
        }
        const d = dayDates[i]!;
        if (isVacDay(d, personVacs))
        {
          projDayCells.push({ value: '-', type: 'vacation', conversations: [] });
          continue;
        }
        const val = projVals[i]!;
        let assigned = false;
        const conversations: ConversationRef[] = [];
        for (const task of projItems)
        {
          if (!task.CARDSD || !task.CARDDD) continue;
          const cs = new Date(task.CARDSD);
          const cd = new Date(task.CARDDD);
          cd.setDate(cd.getDate() + 1);
          if (d >= cs && d < cd)
          {
            if (task.ConversationId)
            {
              conversations.push({
                id: task.ConversationId,
                title:
                  task.Title ||
                  task.CardTitle ||
                  task.ConversationTitle ||
                  `Card #${task.ConversationId}`,
              });
            }
            if (task.IsAssignedCard === 1) assigned = true;
          }
        }
        if (val > 0 && assigned)
          projDayCells.push({ value: val.toFixed(2), type: 'assigned', conversations });
        else if (val > 0)
          projDayCells.push({ value: val.toFixed(2), type: 'white', conversations });
        else projDayCells.push({ value: '0', type: 'white', conversations });
      }

      projectRows.push({ projectId: projId, projectName: projName, dayCells: projDayCells });
    }

    const personDayCells: PersonRow['dayCells'] = [];
    for (let i = 0; i < diffDays; i++)
    {
      if (dayCols[i]!.isWeekend)
      {
        personDayCells.push({ value: '-', type: 'weekend' });
        continue;
      }
      const d = dayDates[i]!;
      if (isVacDay(d, personVacs))
      {
        personDayCells.push({ value: '-', type: 'vacation' });
        continue;
      }
      let total = 0;
      for (const pr of projectRows)
      {
        const c = pr.dayCells[i]!;
        if (c.type !== 'weekend' && c.type !== 'vacation')
        {
          const v = parseFloat(c.value);
          if (!isNaN(v)) total += v;
        }
      }
      personDayCells.push(
        total > 0 ? { value: total.toFixed(2), type: 'assigned' } : { value: '0', type: 'gray' },
      );
    }

    personRows.push({
      personId,
      name,
      team,
      cfValues,
      dayCells: personDayCells,
      projects: projectRows,
    });
  }

  return { dayCols, cfHeaders, personRows };
}

// ─── Date input (same pattern as AttendanceReport) ────────────────────────────

interface AvrDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}
const AvrDateInput = React.forwardRef<HTMLInputElement, AvrDateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="avr-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="avr-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="avr-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
AvrDateInput.displayName = 'AvrDateInput';

// ─── Component ────────────────────────────────────────────────────────────────

const AvailabilityReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);

  // Filter options
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [seniorityOptions, setSeniorityOptions] = useState<SelectOption[]>([]);
  const [levelOptions, setLevelOptions] = useState<SelectOption[]>([]);
  const [resourceTypeOptions, setResourceTypeOptions] = useState<SelectOption[]>([]);
  const [artStyleOptions, setArtStyleOptions] = useState<SelectOption[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<SelectOption[]>([]);
  const [preferenceOptions, setPreferenceOptions] = useState<SelectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);

  const [allEmployees, setAllEmployees] = useState<AssignmentItem[]>([]);

  // Selected filters
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectOption[]>([]);
  const [selectedSeniority, setSelectedSeniority] = useState<SelectOption[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<SelectOption[]>([]);
  const [selectedResourceTypes, setSelectedResourceTypes] = useState<SelectOption[]>([]);
  const [selectedArtStyle, setSelectedArtStyle] = useState<SelectOption[]>([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState<SelectOption[]>([]);
  const [selectedPreferences, setSelectedPreferences] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [includeUnassigned, setIncludeUnassigned] = useState(false);

  // Dates: today ± 15 days (same as legacy default)
  const [fromDate, setFromDate] = useState<Date | null>(() =>
    moment().subtract(15, 'days').startOf('day').toDate(),
  );
  const [toDate, setToDate] = useState<Date | null>(() =>
    moment().add(15, 'days').startOf('day').toDate(),
  );
  const [fromDateEmpty, setFromDateEmpty] = useState(false);
  const [toDateEmpty, setToDateEmpty] = useState(false);
  const [fromDateLess, setFromDateLess] = useState(false);
  const [dateRangeTooShort, setDateRangeTooShort] = useState(false);

  // Table
  const [dayCols, setDayCols] = useState<DayCol[]>([]);
  const [cfHeaders, setCfHeaders] = useState<string[]>([]);
  const [personRows, setPersonRows] = useState<PersonRow[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState('');
  const [hasData, setHasData] = useState(false);

  // Cell detail modal
  interface CellModal {
    dayLabel: string;
    conversations: ConversationRef[];
  }
  const [cellModal, setCellModal] = useState<CellModal | null>(null);
  const [cellModalCards, setCellModalCards] = useState<ConversationCard[]>([]);
  const [cellModalLoading, setCellModalLoading] = useState(false);
  const cellModalRef = useRef<HTMLDivElement>(null);

  // Date range notification
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { openInfoWindow } = useInfoWindow();
  const socketConnectionId = useSocketConnectionId();

  const teamMapRef = useRef<Map<number, string>>(new Map());

  const [projectsPermitted, setProjectsPermitted] = useState<{ ProjectId: number }[]>([]);
  const editCardRef = useRef<any>(null);
  const editCardInitRef = useRef<((conv: any, itemType: any) => void) | null>(null);

  // Close cell modal on outside click
  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetProjectsByConfigValueId(28)
      .then(data => setProjectsPermitted(data || []))
      .catch(() => {});
  }, []);

  const handleCreateQuestion = useCallback((projectId: number) =>
  {
    const sideMenu = getSideMenuLocal() ?? [];
    const questionItem = sideMenu.find((m: any) => m.ConfigValueID === 28);
    const itemType = questionItem
      ? { ...questionItem, ConfigValueId: 28, ConfigValueID: 28 }
      : { ConfigValueId: 28, ConfigValueID: 28, Text: 'Question', Icon: '' };
    const conv = { ProjectId: projectId, IsFromAssignmentReport: true };
    editCardInitRef.current?.(conv, itemType);
    editCardRef.current?.openModal();
  }, []);

  useEffect(() =>
  {
    if (!cellModal) return;
    const handler = (e: MouseEvent) =>
    {
      if (cellModalRef.current && !cellModalRef.current.contains(e.target as Node))
      {
        setCellModal(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cellModal]);

  // Fetch full conversation details when modal opens
  useEffect(() =>
  {
    if (!cellModal || cellModal.conversations.length === 0)
    {
      setCellModalCards([]);
      return;
    }
    let cancelled = false;
    setCellModalLoading(true);
    const ids = cellModal.conversations.map(c => c.id).join(',');
    ReportService.GetConversationsForReportAssigment({ strConversationsIds: ids })
      .then(data =>
      {
        if (!cancelled) setCellModalCards(data || []);
      })
      .catch(() =>
      {
        if (!cancelled) setCellModalCards([]);
      })
      .finally(() =>
      {
        if (!cancelled) setCellModalLoading(false);
      });
    return () =>
    {
      cancelled = true;
    };
  }, [cellModal]);

  // ── Load filter options ──────────────────────────────────────────────────

  useEffect(() =>
  {
    ReportService.GetAllTeams()
      .then((data: any[]) =>
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value }))),
      )
      .catch(() => {});

    ReportService.GetAllAsignedTo({
      Teams: '',
      GetTeamId: true,
      GetDepartmentId: false,
      GetPositionId: false,
      GetAllPeople: false,
    })
      .then((data: any[]) =>
      {
        setAllEmployees(data || []);
        const map = new Map<number, string>();
        for (const e of data || [])
        {
          if (e.TeamId && e.Team) map.set(e.TeamId, e.Team);
        }
        teamMapRef.current = map;
        setEmployeeOptions((data || []).map((e: any) => ({ value: e.PersonID, label: e.Name })));
      })
      .catch(() => {});

    ReportService.GetAssignmentReportFilter({ GroupByDetailId: 1 })
      .then((d: any[]) =>
        setSeniorityOptions((d || []).map(x => ({ value: x.ValueID, label: x.ValueField }))),
      )
      .catch(() => {});
    ReportService.GetAssignmentReportFilter({ GroupByDetailId: 2 })
      .then((d: any[]) =>
        setLevelOptions((d || []).map(x => ({ value: x.ValueID, label: x.ValueField }))),
      )
      .catch(() => {});
    ReportService.GetAssignmentReportFilter({ GroupByDetailId: 3 })
      .then((d: any[]) =>
        setResourceTypeOptions((d || []).map(x => ({ value: x.ValueID, label: x.ValueField }))),
      )
      .catch(() => {});
    ReportService.GetAssignmentReportFilter({ GroupByDetailId: 4 })
      .then((d: any[]) =>
        setArtStyleOptions((d || []).map(x => ({ value: x.ValueID, label: x.ValueField }))),
      )
      .catch(() => {});
    ReportService.GetAssignmentReportFilter({ GroupByDetailId: 5 })
      .then((d: any[]) =>
        setSpecialtyOptions((d || []).map(x => ({ value: x.ValueID, label: x.ValueField }))),
      )
      .catch(() => {});
    ReportService.GetAssignmentReportFilter({ GroupByDetailId: 6 })
      .then((d: any[]) =>
        setPreferenceOptions((d || []).map(x => ({ value: x.ValueID, label: x.ValueField }))),
      )
      .catch(() => {});

    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
      {
        const projs = (data?.Projects || []).sort((a: any, b: any) =>
          a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()),
        );
        setProjectOptions(projs.map((p: any) => ({ value: p.ProjectID, label: p.Name })));
      })
      .catch(() => {});
  }, []);

  // ── Reload employees on team change ─────────────────────────────────────

  const loadEmployees = useCallback((teams: (number | string)[]) =>
  {
    ReportService.GetAllAsignedTo({
      Teams: teams.join(','),
      GetTeamId: true,
      GetDepartmentId: false,
      GetPositionId: false,
      GetAllPeople: false,
    })
      .then((data: any[]) =>
      {
        setAllEmployees(data || []);
        setEmployeeOptions((data || []).map((e: any) => ({ value: e.PersonID, label: e.Name })));
      })
      .catch(() => {});
  }, []);

  // ── Validate ─────────────────────────────────────────────────────────────

  const validate = useCallback((): boolean =>
  {
    let valid = true;
    if (!fromDate)
    {
      setFromDateEmpty(true);
      valid = false;
    }
    else setFromDateEmpty(false);
    if (!toDate)
    {
      setToDateEmpty(true);
      valid = false;
    }
    else setToDateEmpty(false);
    if (!fromDate || !toDate) return false;
    if (moment(toDate).isBefore(moment(fromDate)))
    {
      setFromDateLess(true);
      return false;
    }
    setFromDateLess(false);
    if (moment(toDate).diff(moment(fromDate), 'days') < 15)
    {
      setDateRangeTooShort(true);
      return false;
    }
    setDateRangeTooShort(false);
    if (moment(fromDate).isBefore(moment().subtract(2, 'years')))
    {
      if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
      setShowDateRangeMsg(true);
      dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
      return false;
    }
    return valid;
  }, [fromDate, toDate]);

  // ── Apply filters ────────────────────────────────────────────────────────

  const applyFilters = useCallback(async () =>
  {
    if (!validate()) return;
    setLoading(true);
    setPersonRows([]);
    setDayCols([]);
    setCfHeaders([]);
    setHasData(false);
    setNoResults('');
    setExpandedRows(new Set());

    try
    {
      const request = {
        Teams: selectedTeams.map(t => t.value).join(','),
        AsignedTo: selectedEmployees.map(e => e.value).join(','),
        DateTime: moment(fromDate!).format('YYYYMMDD'),
        Duedate: moment(toDate!).format('YYYYMMDD'),
        Seniority: selectedSeniority.map(s => s.value).join(','),
        Levels: selectedLevels.map(l => l.value).join(','),
        ResourcesTypes: selectedResourceTypes.map(r => r.value).join(','),
        ArtStyles: selectedArtStyle.map(a => a.value).join(','),
        Projects: selectedProjects.map(p => p.value).join(','),
        Specialty: selectedSpecialty.map(s => s.value).join(','),
        Preferences: selectedPreferences.map(p => p.value).join(','),
      };

      const assignedItems: AssignmentItem[] = await ReportService.GetAssignmentReport(request);

      let unassigned: AssignmentItem[] = [];
      if (includeUnassigned && selectedProjects.length === 0)
      {
        const assignedIds = new Set((assignedItems || []).map(i => i.PersonID));
        const pool =
          selectedEmployees.length > 0
            ? allEmployees.filter(e => selectedEmployees.some(s => s.value === e.PersonID))
            : allEmployees;
        unassigned = pool.filter(e => !assignedIds.has(e.PersonID));
      }

      if (!assignedItems?.length && !unassigned.length)
      {
        setNoResults('No results found');
        setLoading(false);
        return;
      }

      const allPersonIds = [
        ...new Set([
          ...(assignedItems || []).map(i => i.PersonID),
          ...unassigned.map(u => u.PersonID),
        ]),
      ];

      const vacations: VacationRequest[] = await ReportService.GetVacationRequestByIds({
        PersonIds: allPersonIds.join(','),
        DateFrom: moment(fromDate!).format('YYYY-MM-DD'),
        DateTo: moment(toDate!).format('YYYY-MM-DD'),
      });

      const {
        dayCols: dc,
        cfHeaders: cfh,
        personRows: pr,
      } = processReport(
        assignedItems || [],
        unassigned,
        vacations || [],
        fromDate!,
        toDate!,
        teamMapRef.current,
      );

      setDayCols(dc);
      setCfHeaders(cfh);
      setPersonRows(pr);
      setExpandedRows(new Set(pr.map(p => p.personId)));
      setHasData(pr.length > 0);
      if (!pr.length) setNoResults('No results found');
    }
    catch
    {
      setNoResults('Error loading data');
    }

    setLoading(false);
  }, [
    validate,
    fromDate,
    toDate,
    selectedTeams,
    selectedEmployees,
    selectedSeniority,
    selectedLevels,
    selectedResourceTypes,
    selectedArtStyle,
    selectedSpecialty,
    selectedPreferences,
    selectedProjects,
    includeUnassigned,
    allEmployees,
  ]);

  // Auto-run on mount
  useEffect(() =>
  {
    const t = setTimeout(() => applyFilters(), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Toggle accordion ─────────────────────────────────────────────────────

  const toggleRow = useCallback((personId: number) =>
  {
    setExpandedRows(prev =>
    {
      const next = new Set(prev);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  }, []);

  // ── Cell class ────────────────────────────────────────────────────────────

  const cellClass = (type: CellType): string =>
  {
    switch (type)
    {
      case 'assigned':
        return 'avr-cell--assigned';
      case 'vacation':
        return 'avr-cell--vacation';
      case 'gray':
        return 'avr-cell--gray';
      case 'white':
        return 'avr-cell--white';
      case 'weekend':
        return 'avr-cell--weekend';
      default:
        return '';
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div id="AvailabilityReportPage">
        <h3 className="avr-title">Availability Report</h3>
        <p className="avr-subtitle">
          This report shows the list of users assigned and unassigned to projects and cards. The
          user(s) must be assigned to a Team in the People card in order to be displayed in this
          report.
        </p>

        {/* Filters */}
        <div className="avr-filters">
          <div className="avr-filters-main">
            {/* Row 1: Teams, Employees, Seniority, Levels, Specialty */}
            <div className="avr-filter-row">
              <div className="avr-filter-item">
                <label className="avr-label">Team(s):</label>
                <Select
                  isMulti
                  options={teamOptions}
                  value={selectedTeams}
                  onChange={v =>
                  {
                    const teams = (v as SelectOption[]) || [];
                    setSelectedTeams(teams);
                    setSelectedEmployees([]);
                    loadEmployees(teams.map(t => t.value));
                  }}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Employee(s):</label>
                <Select
                  isMulti
                  options={employeeOptions}
                  value={selectedEmployees}
                  onChange={v => setSelectedEmployees((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Seniority:</label>
                <Select
                  isMulti
                  options={seniorityOptions}
                  value={selectedSeniority}
                  onChange={v => setSelectedSeniority((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Level(s):</label>
                <Select
                  isMulti
                  options={levelOptions}
                  value={selectedLevels}
                  onChange={v => setSelectedLevels((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Specialty(s):</label>
                <Select
                  isMulti
                  options={specialtyOptions}
                  value={selectedSpecialty}
                  onChange={v => setSelectedSpecialty((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
            </div>

            {/* Row 2: From, To, Resource Types, Area of Expertise, Preferences */}
            <div className="avr-filter-row">
              <div className="avr-filter-item avr-filter-item--date">
                <label className="avr-label">From:</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(d: any) => setFromDate(d)}
                  dateFormat="MM/dd/yyyy"
                  customInput={<AvrDateInput onClear={() => setFromDate(null)} />}
                  popperProps={{ strategy: 'fixed' }}
                  showPopperArrow={false}
                  calendarClassName="avr-datepicker-calendar"
                />
                {fromDateEmpty && <span className="avr-validation">Date is required</span>}
                {fromDateLess && <span className="avr-validation">From must be before To</span>}
                {dateRangeTooShort && (
                  <span className="avr-validation">Date range must be greater than 15 days</span>
                )}
              </div>
              <div className="avr-filter-item avr-filter-item--date">
                <label className="avr-label">To:</label>
                <DatePicker
                  selected={toDate}
                  onChange={(d: any) => setToDate(d)}
                  dateFormat="MM/dd/yyyy"
                  customInput={<AvrDateInput onClear={() => setToDate(null)} />}
                  popperProps={{ strategy: 'fixed' }}
                  showPopperArrow={false}
                  calendarClassName="avr-datepicker-calendar"
                />
                {toDateEmpty && <span className="avr-validation">Date is required</span>}
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Resource Type(s):</label>
                <Select
                  isMulti
                  options={resourceTypeOptions}
                  value={selectedResourceTypes}
                  onChange={v => setSelectedResourceTypes((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Area of Expertise:</label>
                <Select
                  isMulti
                  options={artStyleOptions}
                  value={selectedArtStyle}
                  onChange={v => setSelectedArtStyle((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-filter-item">
                <label className="avr-label">Preference(s):</label>
                <Select
                  isMulti
                  options={preferenceOptions}
                  value={selectedPreferences}
                  onChange={v => setSelectedPreferences((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
            </div>

            {/* Row 3: Projects */}
            <div className="avr-filter-row">
              <div className="avr-filter-item">
                <label className="avr-label">Project(s):</label>
                <Select
                  isMulti
                  options={projectOptions}
                  value={selectedProjects}
                  onChange={v => setSelectedProjects((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="avr-checkbox-label avr-checkbox-inline">
                <span className="corgee_com_checkbox">
                  <input
                    id="avr-include-unassigned-cb"
                    type="checkbox"
                    checked={includeUnassigned}
                    onChange={() => setIncludeUnassigned(p => !p)}
                  />
                  <label htmlFor="avr-include-unassigned-cb"></label>
                </span>
                <label htmlFor="avr-include-unassigned-cb" className="avr-checkbox-text">
                  Include Unassigned
                </label>
              </div>
              <button
                className="avr-apply-btn avr-apply-btn--inline"
                onClick={applyFilters}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
          </div>

          {/* Legend column on the right */}
          <div className="avr-legend">
            <span className="avr-legend-btn avr-legend-btn--available">Available</span>
            <span className="avr-legend-btn avr-legend-btn--assigned">
              Assigned to a Project or Task
            </span>
            <span className="avr-legend-btn avr-legend-btn--daysoff">Days Off</span>
          </div>
        </div>

        {/* Table */}
        {(hasData || loading || noResults) && (
          <div className="avr-table-wrapper">
            <div className="avr-table-scroll">
              <table className="avr-table">
                <thead>
                  <tr>
                    <th className="avr-th--sticky avr-th--col1">&nbsp;</th>
                    <th className="avr-th--sticky avr-th--col2">Employee</th>
                    <th className="avr-th--sticky avr-th--col3">Team</th>
                    {cfHeaders.map(key => (
                      <th key={key} className="avr-th--cf">
                        {cfKeyToHeader(key)}
                      </th>
                    ))}
                    {dayCols.map((col, i) => (
                      <th key={i} className="avr-th--day">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3 + cfHeaders.length + dayCols.length} className="avr-no-data">
                        Loading...
                      </td>
                    </tr>
                  )}
                  {!loading && !hasData && noResults && (
                    <tr>
                      <td colSpan={3 + cfHeaders.length + dayCols.length} className="avr-no-data">
                        {noResults}
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    personRows.map(person =>
                    {
                      const expanded = expandedRows.has(person.personId);
                      return (
                        <React.Fragment key={person.personId}>
                          {/* Person row */}
                          <tr className="avr-row--person">
                            <td
                              className="avr-td--col1"
                              onClick={() => toggleRow(person.personId)}
                              style={{ cursor: 'pointer', textAlign: 'center' }}
                            >
                              {person.projects.length > 0 &&
                                <div
                                  className={`avr-expand-icon${expanded ? ' avr-expand-icon--open' : ''}`}
                                />
                              }
                            </td>
                            <td className="avr-td--col2">{person.name}</td>
                            <td className="avr-td--col3">{person.team || '-'}</td>
                            {person.cfValues.map((v, ci) => (
                              <td
                                key={ci}
                                className="avr-td--cf"
                                title={v.length > 40 ? v : undefined}
                              >
                                {v.length > 40 ? v.slice(0, 39) + '…' : v}
                              </td>
                            ))}
                            {person.dayCells.map((cell, di) => (
                              <td
                                key={di}
                                className={
                                  cell.type === 'vacation'
                                    ? 'avr-cell--vacation-person'
                                    : cellClass('gray')
                                }
                              >
                                {cell.value}
                              </td>
                            ))}
                          </tr>

                          {/* Project sub-rows */}
                          {expanded &&
                            person.projects.map(proj =>
                            {
                              const validateProject = projectsPermitted.find(
                                p => p.ProjectId === proj.projectId,
                              );
                              const hasPermission =
                                validateProject != null && validateProject.ProjectId > 0;
                              return (
                                <tr key={proj.projectId} className="avr-row--project">
                                  <td className="avr-td--col1">&nbsp;</td>
                                  <td
                                    className="avr-td--proj-name"
                                    colSpan={2}
                                    title={
                                      proj.projectName.length > 30 ? proj.projectName : undefined
                                    }
                                  >
                                    {proj.projectName.length > 30
                                      ? proj.projectName.slice(0, 29) + '…'
                                      : proj.projectName}
                                  </td>
                                  {cfHeaders.map((_, ci) => (
                                    <td key={ci} className="avr-td--cf">
                                      &nbsp;
                                    </td>
                                  ))}
                                  {proj.dayCells.map((cell, di) =>
                                  {
                                    const clickable =
                                      cell.conversations.length > 0 &&
                                      cell.value !== '0' &&
                                      cell.value !== '-';
                                    const showBtn =
                                      hasPermission &&
                                      cell.type !== 'weekend' &&
                                      cell.type !== 'vacation';
                                    return (
                                      <td
                                        key={di}
                                        className={`${cellClass(cell.type)}${clickable ? ' avr-cell--clickable' : ''}`}
                                        onClick={
                                          clickable
                                            ? () =>
                                              setCellModal({
                                                dayLabel: dayCols[di]!.label,
                                                conversations: cell.conversations,
                                              })
                                            : undefined
                                        }
                                      >
                                        {showBtn && (
                                          <div className="avr-btn-zone">
                                            <div
                                              className={`avr-add-new-btn${cell.type === 'assigned' ? ' avr-add-new-btn--white' : ''}`}
                                              title="Add Question"
                                              onClick={e =>
                                              {
                                                e.stopPropagation();
                                                handleCreateQuestion(proj.projectId);
                                              }}
                                            />
                                          </div>
                                        )}
                                        <div>{cell.value}</div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cell detail modal */}
        {cellModal && (
          <div className="avr-cell-modal-overlay">
            <div className="avr-cell-modal" ref={cellModalRef}>
              <div className="avr-cell-modal-header">
                <span className="avr-cell-modal-title">Tasks: {cellModal.dayLabel}</span>
                <button
                  type="button"
                  className="avr-cell-modal-close"
                  onClick={() => setCellModal(null)}
                >
                  ×
                </button>
              </div>
              <ul className="avr-cell-modal-list">
                {cellModalLoading && (
                  <li className="avr-cell-modal-item avr-cell-modal-loading">Loading…</li>
                )}
                {!cellModalLoading &&
                  cellModalCards.map(card => (
                    <li
                      key={card.ConversationId}
                      className="avr-cell-modal-item avr-cell-modal-item--link"
                      onClick={() => openInfoWindow(card.ConversationId)}
                    >
                      <i className={`${card.Icon || 'fa fa-id-card-o'} avr-cell-modal-icon`} />
                      <span>{card.Title}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <EditCard
        ref={editCardRef}
        config={{
          apiUrl: config.api.baseUrl,
          authToken: authStorage.getAccessToken() || '',
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            editCardInitRef.current = fns.initConfig;
          },
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />

      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
            @keyframes avr-date-msg {
              0%   { opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'avr-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default AvailabilityReport;
